// Pure Part D phase-timeline projector — 12 monthly rows for a member's
// drug basket on one plan. Deductible → initial → catastrophic, mid-
// month straddle, per-drug insulin cap, ACIP vaccines at $0.
//
// Zero I/O. No DB, no network, no side-effects — call it from an agent
// screen, a consumer screen, a batch job, a test.
//
// ⚠ CROSS-REPO SYNC — this file exists at two locations:
//   robert9907/planmatch:  api/library/partDTimeline.ts     (agent)
//   robert9907/plan-match: packages/shared/src/partDTimeline.ts (consumer)
// Any change must be mirrored to the other until we publish this
// module as a standalone npm package that both repos depend on. Only
// the import path for planYearParams differs (agent uses `.js`
// suffix; consumer omits it to match its tsconfig). CI job
// partd-drift.yml runs scripts/check-partd-drift.mjs which enforces
// byte-identity after normalizing that suffix.

import { getPlanYearParams, type PlanYearParams } from './planYearParams.js';

// ─── Public types ─────────────────────────────────────────────────────

export type Phase = 'deductible' | 'initial' | 'catastrophic';

/** One drug + how the plan bills it. Callers translate their own
 *  formulary + drug-phase data into this shape. */
export interface DrugFill {
  rxcui: string;
  name: string;
  /** Plan formulary tier (1..5+). null when the drug is off-formulary;
   *  off-formulary drugs are excluded from timeline math (they don't
   *  count toward the deductible or TrOOP under CMS rules). */
  tier: number | null;
  /** Full retail cost of one fill (the plan's negotiated ingredient
   *  cost + dispensing fee). Used for deductible burn, coinsurance
   *  math, and grossDrugCost accounting. */
  monthlyGrossCost: number;
  /** How many fills per plan year. 12 for 30-day retail, 4 for 90-day
   *  mail-order. Any positive integer is accepted; fills are spaced
   *  evenly across the year starting at startMonth. */
  fillsPerYear: number;
  /** Per-drug cost-share override. When set, these phase shares are
   *  used for this fill INSTEAD of plan.tierCostShares[tier]. Needed
   *  for IRA-negotiated drugs (Eliquis, Jardiance, …): Medicare.gov
   *  prices them at a drug-specific MFP cost-share that differs from
   *  the plan's filed tier coinsurance (e.g. H5302-022 files Tier 3 at
   *  24% but Eliquis actually bills ~17% off its negotiated price).
   *  pm_drug_cost_cache carries the true per-fill initial member cost,
   *  which the brain passes through here as an `initial` copay. The
   *  deductible phase is left unset so the member still pays full gross
   *  until the deductible is met. */
  costShareOverride?: TierCostShares;
  /** IRA insulin cap applies (§11406). Member cost per fill is capped
   *  at insulinMonthlyCap regardless of phase. */
  isInsulin?: boolean;
  /** Plan waives the Part D deductible for this drug (Medicare.gov shows a
   *  flat copay in the deductible phase, not the full negotiated price). The
   *  fill then skips the deductible phase — billed at the initial-phase rate
   *  from month one, like insulin. */
  deductibleWaived?: boolean;
  /** ACIP-recommended Part D vaccine (§11401) — member pays
   *  vaccineMemberCost ($0) in every phase. Vaccine spend still counts
   *  toward grossDrugCost. */
  isVaccine?: boolean;
  /** Per-fill manufacturer discount amount that counts toward TrOOP
   *  (in the initial coverage phase for brand drugs, post-IRA §11201).
   *  Callers who don't have discount detail should leave this
   *  unset — TrOOP will underestimate by exactly this amount, which is
   *  the industry-standard approximation. */
  manufacturerDiscountPerFill?: number;
  // Present in the drug-phases request shape (see api/drug-phases.ts).
  // Not used by the math — accepted for API compatibility.
  ndc?: string;
  quantity?: number;
  daysSupply?: number;
}

export type CostShareType = 'copay' | 'coinsurance';

export interface PhaseCostShare {
  type: CostShareType;
  /** Dollars per fill for copay; fraction 0..1 for coinsurance. */
  amount: number;
  /** Optional lower/upper bounds (CMS files these on coinsurance rows
   *  as e.g. "25% coinsurance, min $10, max $150"). */
  min?: number;
  max?: number;
}

export interface TierCostShares {
  deductible?: PhaseCostShare;
  initial?: PhaseCostShare;
  catastrophic?: PhaseCostShare;
}

export interface PlanInput {
  /** Plan's filed Part D annual deductible ($). Zero means no
   *  deductible; capped by planYearParams.partDDeductibleMax. */
  deductible: number;
  /** Tiers subject to the deductible. CMS lets plans waive deductible
   *  on tier 1 and tier 2 (very common); some waive tiers 1..3. Fills
   *  of tiers not in this list skip the deductible phase entirely and
   *  bill at the initial-phase rate from month one. */
  deductibleAppliesToTiers: number[];
  /** Per-tier cost-share table. Missing tier or missing phase entry →
   *  treated as $0 to the member for that phase (i.e., a filing gap
   *  favors the beneficiary rather than penalizing them). */
  tierCostShares: Record<number, TierCostShares>;
}

export interface PartDTimelineArgs {
  drugs: ReadonlyArray<DrugFill>;
  plan: PlanInput;
  planYear: number;
  /** Optional override — when omitted, params are pulled from
   *  planYearParams.ts by planYear. Injectable for tests and for a
   *  future DB-backed params table without touching the function. */
  planYearParams?: PlanYearParams;
  /** Starting calendar month for the projection (1..12). Defaults to
   *  January. For mid-year enrollment quotes, callers pass the month
   *  the member's coverage begins. Months before startMonth are
   *  omitted from the returned array. */
  startMonth?: number;
}

export interface MonthlyRow {
  /** Calendar month 1..12. */
  month: number;
  /** Phase the month ENDS in. When phaseChangedMidMonth is true, this
   *  is the newer phase (the one the closing balance sits in). */
  phase: Phase;
  /** Member out-of-pocket spend attributable to this month. */
  memberCost: number;
  /** Running total of memberCost from startMonth through this month
   *  inclusive. */
  cumulativeMemberCost: number;
  /** True out-of-pocket accumulator — member cost + manufacturer
   *  discount contributions. Gates catastrophic transition. */
  troop: number;
  /** Total plan-negotiated drug cost this month (member + plan +
   *  manufacturer). Reported for transparency; does NOT gate phase. */
  grossDrugCost: number;
  /** True when at least one fill this month straddled a phase
   *  boundary (member paid at two different rates within the month). */
  phaseChangedMidMonth: boolean;
  /** Member cost this month per drug, index-aligned to args.drugs (input
   *  order). Sums to memberCost (before rounding). Drives the per-drug
   *  rows of the Jan–Dec drug-cost calendar. */
  memberCostByDrug: number[];
}

// ─── Internal state ───────────────────────────────────────────────────

interface RunState {
  deductibleRemaining: number;
  troop: number;
  cumulativeMemberCost: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────

function isFillMonth(
  drug: DrugFill,
  month: number,
  startMonth: number,
): boolean {
  if (drug.fillsPerYear <= 0) return false;
  if (month < startMonth) return false;
  const activeMonths = 12 - startMonth + 1;
  if (drug.fillsPerYear >= activeMonths) return true;
  // Space fills evenly across the remaining year, anchored to startMonth.
  //   fillsPerYear=4, startMonth=1 → months 1, 4, 7, 10
  //   fillsPerYear=4, startMonth=3 → months 3, 6, 9, 12
  const stride = Math.floor(activeMonths / drug.fillsPerYear);
  const offset = month - startMonth;
  return offset % stride === 0 && offset / stride < drug.fillsPerYear;
}

/** What the plan charges for a full fill of this drug in a given
 *  phase. `remainingGross` lets coinsurance be computed on a partial
 *  fill (mid-month straddle: the initial-phase piece is only part of
 *  the fill). For copay tiers the copay is scaled proportionally to
 *  the share of the fill in this phase, since a flat copay applied to
 *  a straddle piece would over-charge (the pharmacy bills the copay
 *  once for the whole fill, not once per phase piece). */
function memberCostForPhasePiece(args: {
  share: PhaseCostShare | undefined;
  fullFillGross: number;
  pieceGross: number;
  fallbackToRetail: boolean;
}): number {
  const { share, fullFillGross, pieceGross, fallbackToRetail } = args;
  if (pieceGross <= 0) return 0;
  if (!share) {
    // No filed cost share for this tier+phase. Deductible pieces bill
    // at retail (that's the whole point of the deductible); initial
    // pieces with a missing filing are treated as $0 (favor the
    // beneficiary — a filing gap shouldn't invent a charge).
    return fallbackToRetail ? pieceGross : 0;
  }
  if (share.type === 'coinsurance') {
    const raw = pieceGross * share.amount;
    return clampToMinMax(raw, share.min, share.max, pieceGross);
  }
  // Copay — scale proportionally to the share of the fill in this
  // phase so a straddle doesn't double-bill.
  const proportional = fullFillGross > 0
    ? share.amount * (pieceGross / fullFillGross)
    : share.amount;
  return clampToMinMax(proportional, share.min, share.max, pieceGross);
}

function clampToMinMax(
  value: number,
  min: number | undefined,
  max: number | undefined,
  cap: number,
): number {
  let v = value;
  if (min != null) v = Math.max(v, min);
  if (max != null) v = Math.min(v, max);
  // Member can never pay more than the drug actually costs.
  return Math.max(0, Math.min(v, cap));
}

function tierCanUseDeductible(tier: number | null, plan: PlanInput): boolean {
  if (tier == null) return false;
  return plan.deductibleAppliesToTiers.includes(tier);
}

/** Whether a drug pays the full negotiated price during the deductible phase
 *  (a deductible-SUBJECT drug), vs. paying its normal initial-phase share even
 *  while the deductible is being met (insulin §11406 / waived / exempt tier). */
function drugPaysFullDuringDeductible(drug: DrugFill, plan: PlanInput): boolean {
  return tierCanUseDeductible(drug.tier, plan) && !drug.isInsulin && !drug.deductibleWaived;
}

// ─── Per-fill charge ──────────────────────────────────────────────────
//
// Handles a single fill event: splits across phases as needed, applies
// insulin cap and vaccine override, updates state, returns the fill's
// member cost and whether the fill straddled a boundary.

interface FillResult {
  memberCost: number;
  grossCost: number;
  straddled: boolean;
}

/** Charges one fill event and mutates state in place: draws down
 *  deductibleRemaining, adds to troop, and returns per-fill totals. */
function chargeFill(
  drug: DrugFill,
  state: RunState,
  plan: PlanInput,
  params: PlanYearParams,
): FillResult {
  const gross = Math.max(0, drug.monthlyGrossCost);
  const result: FillResult = {
    memberCost: 0,
    grossCost: gross,
    straddled: false,
  };

  // Off-formulary → member pays retail out of their own pocket, but
  // it doesn't accumulate toward the deductible or TrOOP under CMS
  // rules. Report as gross drug cost only.
  if (drug.tier == null || gross === 0) return result;

  // Vaccines: $0 to member (IRA §11401), no TrOOP contribution, gross
  // still counted.
  if (drug.isVaccine) {
    result.memberCost = params.vaccineMemberCost;
    return result;
  }

  // Already catastrophic — member cost is $0 (IRA §11201, 2025+).
  if (state.troop >= params.troopCap) return result;

  // Per-drug override (IRA-negotiated drugs) wins over the plan's filed
  // tier cost-share. See DrugFill.costShareOverride.
  const tierShares: TierCostShares | undefined =
    drug.costShareOverride ?? plan.tierCostShares[drug.tier];
  // Whether THIS drug pays the full negotiated price during the deductible
  // phase. Covered insulin (IRA §11406 — $35/month cap from fill one),
  // deductible-waived drugs, and drugs on a plan's deductible-exempt tiers do
  // NOT: they pay their normal initial-phase cost-share even while the
  // deductible is being met. (Routing insulin at the capped $35 through the
  // deductible would overstate it — Lantus on H4523-024: $256 vs $148 PF.)
  const paysFullDuringDeductible = drugPaysFullDuringDeductible(drug, plan);

  // ── Split fill across phases ────────────────────────────────────
  // The Part D deductible is a shared TOTAL-DRUG-COST threshold ($700 of
  // negotiated price, not member out-of-pocket). EVERY covered drug's gross
  // draws it down — including drugs that don't pay full price during it: the
  // plan's payment on a $0-copay exempt-tier drug (or insulin, or a waived-
  // deductible drug) still counts toward meeting the deductible. This mirrors
  // medicare.gov: a deductible-SUBJECT drug (Linzess tier 3 on H3449-027)
  // clears the deductible faster — and costs the member less — when filled
  // alongside exempt drugs, because their spend helps meet the $700. The old
  // code only let subject drugs draw the deductible down, so a lone subject
  // drug absorbed the whole $700 at full price even after the basket's total
  // spend had already met it (A03 $899 vs PF $563; A02 pegged the $2,400 cap).
  let deductiblePiece = 0;
  let initialPiece = gross;
  if (state.deductibleRemaining > 0) {
    deductiblePiece = Math.min(gross, state.deductibleRemaining);
    initialPiece = gross - deductiblePiece;
    state.deductibleRemaining -= deductiblePiece;
  }

  const deductibleMemberCost = memberCostForPhasePiece({
    // A deductible-subject drug pays the full negotiated price for its
    // deductible piece; an exempt / insulin / waived drug pays its normal
    // initial-phase share even here (its gross still drew the deductible down
    // above, but the member isn't charged full price for it).
    share: paysFullDuringDeductible ? tierShares?.deductible : tierShares?.initial,
    fullFillGross: gross,
    pieceGross: deductiblePiece,
    fallbackToRetail: paysFullDuringDeductible,
  });

  const initialMemberCost = memberCostForPhasePiece({
    share: tierShares?.initial,
    fullFillGross: gross,
    pieceGross: initialPiece,
    fallbackToRetail: false,
  });

  let memberCost = deductibleMemberCost + initialMemberCost;
  let troopAdd = memberCost + (drug.manufacturerDiscountPerFill ?? 0);

  // ── Catastrophic mid-fill crossing ──────────────────────────────
  // If TrOOP would jump past the cap during this fill, refund the
  // over-cap portion of the initial piece — the pharmacy would have
  // billed the initial rate up to the crossing point, then $0. The
  // deductible piece is unaffected (deductible is fully due before
  // TrOOP counting for catastrophic even matters).
  const projectedTroop = state.troop + troopAdd;
  if (projectedTroop > params.troopCap && initialMemberCost > 0) {
    const overshoot = projectedTroop - params.troopCap;
    const refund = Math.min(overshoot, initialMemberCost);
    memberCost -= refund;
    troopAdd -= refund;
    result.straddled = true;
  }

  // ── Insulin cap ──────────────────────────────────────────────────
  if (drug.isInsulin && memberCost > params.insulinMonthlyCap) {
    const capped = params.insulinMonthlyCap;
    troopAdd -= (memberCost - capped);
    memberCost = capped;
  }

  // Straddle when both a deductible piece AND an initial piece were
  // charged in the same fill (member paid at two different rates).
  if (deductiblePiece > 0 && initialPiece > 0) result.straddled = true;

  state.troop += troopAdd;
  result.memberCost = memberCost;
  return result;
}

// ─── Public entry ─────────────────────────────────────────────────────

export function partDTimeline(args: PartDTimelineArgs): MonthlyRow[] {
  const startMonth = args.startMonth ?? 1;
  if (startMonth < 1 || startMonth > 12) {
    throw new Error(`partDTimeline: startMonth must be 1..12 (got ${startMonth})`);
  }
  const params = args.planYearParams ?? getPlanYearParams(args.planYear);

  // If no drug in the basket can burn the deductible (basket is
  // entirely on waived tiers), report the whole year as initial-phase
  // coverage rather than "we're in deductible but nobody's spending
  // toward it" — that would color the UI misleadingly.
  const anyDrugSubjectToDeductible = args.drugs.some((d) =>
    tierCanUseDeductible(d.tier, args.plan) && !d.isInsulin && !d.deductibleWaived,
  );
  const effectiveDeductible = anyDrugSubjectToDeductible
    // Plan's filed deductible is capped by the year's max (a plan
    // filing above the max is a data error — clamp defensively).
    ? Math.min(Math.max(0, args.plan.deductible), params.partDDeductibleMax)
    : 0;

  const state: RunState = {
    deductibleRemaining: effectiveDeductible,
    troop: 0,
    cumulativeMemberCost: 0,
  };

  const rows: MonthlyRow[] = [];

  // Charge fills toward the shared deductible in ASCENDING TIER order each
  // month. medicare.gov draws the deductible by tier number, so the LOWER-tier
  // drug's fill counts toward the pot first — regardless of whether it's
  // deductible-subject or exempt. Verified live 2026-10-28 on two cases a pure
  // subject-first (or exempt-first) sort gets wrong:
  //   • A08 H5453-002: Eliquis tier-3 subject + atorvastatin/finasteride tier-1
  //     EXEMPT. Tier-1 first → the $14 generics deplete part of the $461
  //     deductible before Eliquis, so Eliquis under-pays → $801.81 (= PF). A
  //     subject-first sort charged Eliquis first → $826.81 (wrong).
  //   • A03 H3449-027: Linzess tier-3 subject + losartan/alendronate tier-6
  //     exempt. Tier-3 first → Linzess pays full for 2 months → $562.72 (= PF).
  // Ascending-tier reproduces BOTH; subject-first only reproduced A03 (where the
  // subject drug happens to be the lowest tier). Stable sort preserves input
  // order within a tier; null tiers (Part B / uncovered) sort last.
  // MIRROR of consumer plan-match packages/shared/src/partDTimeline.ts.
  const orderedDrugs = args.drugs
    .map((drug, index) => ({ drug, index }))
    .sort((a, b) => (a.drug.tier ?? Number.POSITIVE_INFINITY) - (b.drug.tier ?? Number.POSITIVE_INFINITY));

  for (let month = startMonth; month <= 12; month += 1) {
    let monthMemberCost = 0;
    let monthGrossCost = 0;
    let anyStraddle = false;
    const byDrug = new Array<number>(args.drugs.length).fill(0);

    const phaseAtStart = currentPhase(state, params);

    // chargeFill mutates state (deductibleRemaining, troop) so
    // subsequent fills in the same month see the running balance —
    // a $600 fill followed by a $50 fill on the same day should have
    // the second fill see the deductible burn from the first.
    for (const { drug, index } of orderedDrugs) {
      if (!isFillMonth(drug, month, startMonth)) continue;
      const fill = chargeFill(drug, state, args.plan, params);
      byDrug[index] += fill.memberCost;
      monthMemberCost += fill.memberCost;
      monthGrossCost += fill.grossCost;
      if (fill.straddled) anyStraddle = true;
    }

    state.cumulativeMemberCost += monthMemberCost;

    const phaseAtEnd = currentPhase(state, params);
    const phaseChangedMidMonth =
      anyStraddle || phaseAtStart !== phaseAtEnd;

    rows.push({
      month,
      phase: phaseAtEnd,
      memberCost: round2(monthMemberCost),
      cumulativeMemberCost: round2(state.cumulativeMemberCost),
      troop: round2(state.troop),
      grossDrugCost: round2(monthGrossCost),
      phaseChangedMidMonth,
      memberCostByDrug: byDrug.map(round2),
    });
  }

  return rows;
}

function currentPhase(state: RunState, params: PlanYearParams): Phase {
  if (state.troop >= params.troopCap) return 'catastrophic';
  if (state.deductibleRemaining > 0) return 'deductible';
  return 'initial';
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
